import * as React from "react";
import {
  AutocompleteInput,
  BooleanInput,
  ReferenceInput,
  SelectArrayInput,
  SelectInput,
  TextInput,
  useGetList,
  useInput,
} from "react-admin";
import { useForm } from "react-final-form";
import { ReadOnlyField } from "lib/components/ReadOnlyField";
import { DateTimeInput } from "lib/components/DateTime";
import { Typography, TextField, IconButton, Button } from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import DeleteIcon from "@material-ui/icons/Delete";

export function DetailFields({ isCreate = false }) {
  const form = useForm();

  const { data: stylesData } = useGetList("classStyles", { page: 1, perPage: 100 }, { field: "name", order: "ASC" });
  const styleChoices = stylesData
    ? Object.values(stylesData).map((s) => ({ id: s.name, name: s.name }))
    : [];

  const { data: catData } = useGetList("categories", { page: 1, perPage: 100 }, { field: "name", order: "ASC" });
  const catChoices = catData
    ? Object.values(catData).map((c) => ({ id: c.slug, name: c.name }))
    : [];

  const { data: instructorData } = useGetList("instructors", { page: 1, perPage: 500 }, { field: "name", order: "ASC" });
  const instructorChoices = instructorData
    ? Object.values(instructorData).map((i) => ({ id: i.slug, name: i.name }))
    : [];

  const handleInstructorChange = (slug) => {
    if (!slug) {
      form.change("instructorName", "");
      return;
    }
    const instructor = instructorChoices.find((i) => i.id === slug);
    if (instructor) {
      form.change("instructorName", instructor.name);
    }
  };

  return (
    <>
      {isCreate ? (
        <TextInput
          source="slug"
          fullWidth
          helperText="URL-friendly identifier (e.g. house-beginner). Lowercase, hyphens only."
        />
      ) : (
        <ReadOnlyField source="slug" addLabel label="Slug" />
      )}
      <TextInput source="title" fullWidth />
      <div style={{ display: "flex", gap: 16 }}>
        <ReferenceInput
          label="Level"
          source="level"
          reference="classLevels"
          style={{ flex: 1 }}
        >
          <SelectInput allowNull optionText="name" />
        </ReferenceInput>
        <SelectInput source="style" choices={styleChoices} allowEmpty style={{ flex: 1 }} />
      </div>
      <SelectArrayInput source="categories" choices={catChoices} fullWidth />
      <div style={{ display: "flex", gap: 16 }}>
        <div style={{ flex: 1 }}>
          <AutocompleteInput
            source="instructorSlug"
            choices={instructorChoices}
            optionText="name"
            label="Instructor"
            onChange={handleInstructorChange}
            fullWidth
          />
        </div>
        <div style={{ flex: 1 }}>
          <TextInput source="instructorName" label="Instructor Name" disabled fullWidth />
        </div>
      </div>
      <BooleanInput source="isFree" label="Is Free" />
      <DateTimeInput source="publishDate" />
      <TextInput
        source="averageSessionTime"
        fullWidth
        helperText='e.g. "45 min/day"'
      />
      <RelatedClassesField />
    </>
  );
}

function RelatedClassesField() {
  const {
    input: { value, onChange },
  } = useInput({ source: "relatedClasses" });

  const classIds = value || [];

  const addClassId = () => {
    onChange([...classIds, 0]);
  };

  const updateClassId = (index, val) => {
    const updated = [...classIds];
    updated[index] = parseInt(val, 10) || 0;
    onChange(updated);
  };

  const removeClassId = (index) => {
    onChange(classIds.filter((_, i) => i !== index));
  };

  return (
    <div style={{ marginTop: 24 }}>
      <Typography variant="subtitle1" gutterBottom>
        Related Classes
      </Typography>
      <Typography variant="body2" color="textSecondary" gutterBottom>
        Class IDs shown under &quot;Enjoyed this program? Check out these similar classes!&quot;
      </Typography>
      {classIds.map((id, idx) => (
        <div
          key={`related-${idx}`} // eslint-disable-line react/no-array-index-key
          style={{ display: "flex", alignItems: "center", marginBottom: 8 }}
        >
          <TextField
            label={`Class ID #${idx + 1}`}
            type="number"
            value={id || ""}
            onChange={(e) => updateClassId(idx, e.target.value)}
            size="small"
            style={{ width: 160, marginRight: 8 }}
          />
          <IconButton size="small" onClick={() => removeClassId(idx)} color="secondary">
            <DeleteIcon fontSize="small" />
          </IconButton>
        </div>
      ))}
      <Button size="small" startIcon={<AddIcon />} onClick={addClassId}>
        Add Related Class
      </Button>
    </div>
  );
}
